import {
    RepresentativeSection2Sign,
    RepresentativeSection2SignRequest
} from '@dtos/remote-i9-sign';
import Joi from '@hapi/joi';
import { ISection2Certification } from '@models/onboarding-review/section2-certification';
import { ISection2CompleteForm } from '@models/onboarding-review/section2-complete-form';
import { Section2ValidationErrors } from '@models/onboarding-review/section2-validation-errors';
import { IRemoteRepresentativeService } from '@services/remote-representative-service';
import { ISection2CertificationStore } from '@stores/onboarding-review/section2-certification-store';
import { ISection2CompleteStore } from '@stores/onboarding-review/section2-complete-store';
import {
    DateFormatTemplate,
    formatAsLocalTimezone
} from '@utils/date-format';
import {
    getCurrentISOTimestamp,
    getLocalTimezone
} from '@utils/date-query';
import { find } from 'lodash';

export enum ESignResult {
    InActive,
    Success,
    Failed,
    InvalidRequest
}

type ESignValidationErrors = Omit<
    Record<Section2ValidationErrors, boolean>,
    Section2ValidationErrors.documentDetailMissing | Section2ValidationErrors.documentNumberDoesNotMatchSection1
>;

export interface IESignRequestService {
    isRequestValid(): boolean;
    getValidationErrors(): ESignValidationErrors;
    requestSection2SignAsync(): Promise<ESignResult>;
}

export class ESignRequestService implements IESignRequestService {
    constructor(
        // time limited work around to access the section 2 observables to make the sign request
        private section2CompleteStore: ISection2CompleteStore,
        // time limited work around to access the section 2 observables to make the sign request
        private section2CertificationStore: ISection2CertificationStore,
        private remoteRepresentativeService: IRemoteRepresentativeService
    ) {
    }

    private validateCompleteStore(store: ISection2CompleteStore, validationOptions?: Joi.ValidationOptions): Joi.ValidationResult {
        const schema = Joi.object<any, ISection2CompleteForm>({
            acceptedElectronicSignature: Joi.boolean()
                .required()
                .valid(true),
            documentsPhysicallyExamined: Joi.boolean()
                .required()
                .valid(true)
        });

        return schema.validate({
            acceptedElectronicSignature: store.acceptedElectronicSignature,
            documentsPhysicallyExamined: store.documentsPhysicallyExamined
        }, validationOptions);
    }

    private validateCertificationStore(store: ISection2CertificationStore, validationOptions?: Joi.ValidationOptions): Joi.ValidationResult {
        const currentFormattedDate = formatAsLocalTimezone(
            getCurrentISOTimestamp(),
            DateFormatTemplate.MonthFirstCalendarDate
        );
        const schema = Joi.object<any, ISection2Certification>({
            firstName: Joi.string()
                .required()
                .min(1),
            lastName: Joi.string()
                .required()
                .min(1),
            didAgreeToSign: Joi.boolean()
                .required()
                .valid(true),
            signedOn: Joi.string()
                .valid(currentFormattedDate)
        });

        return schema.validate({
            firstName: store.firstName,
            lastName: store.lastName,
            didAgreeToSign: store.didAgreeToSign,
            signedOn: store.signedOn
        }, validationOptions);
    }

    private createRequest(): RepresentativeSection2SignRequest {
        const representativeFirstName = this.section2CertificationStore.firstName;
        const representativeLastName = this.section2CertificationStore.lastName;
        const localTimezone = getLocalTimezone();
        const section2Sign: RepresentativeSection2Sign = {
            authorizedRepresentativeFirstName: representativeFirstName,
            authorizedRepresentativeLastName: representativeLastName,
            clientTimezone: localTimezone,
            confirmDocumentsPhysicallyExamined: this.section2CompleteStore.documentsPhysicallyExamined
        };

        return {
            section2Sign,
            confirmAgreeToSign: this.section2CertificationStore.didAgreeToSign,
            confirmReadAndAcceptElectronicSignature: this.section2CompleteStore.acceptedElectronicSignature,
            representativeFullName: `${representativeFirstName} ${representativeLastName}`,
            signedOn: getCurrentISOTimestamp()
        };
    }

    getValidationErrors(): ESignValidationErrors {
        const completeStoreValidation = this.validateCompleteStore(
            this.section2CompleteStore,
            { abortEarly: false }
        );
        const certificationStoreValidation = this.validateCertificationStore(
            this.section2CertificationStore,
            { abortEarly: false }
        );

        const findErrorWithKey = (errorItems: Joi.ValidationErrorItem[] | undefined, key: string): boolean => (
            find(errorItems, { context: { key } }) !== undefined
        );

        return {
            [Section2ValidationErrors.confirmDocumentsPhysicallyExamined]: findErrorWithKey(completeStoreValidation.error?.details, 'documentsPhysicallyExamined'),
            [Section2ValidationErrors.confirmAgreeToSign]: findErrorWithKey(certificationStoreValidation.error?.details, 'didAgreeToSign'),
            [Section2ValidationErrors.provideSignDate]: findErrorWithKey(certificationStoreValidation.error?.details, 'signedOn'),
            [Section2ValidationErrors.provideLastName]: findErrorWithKey(certificationStoreValidation.error?.details, 'lastName'),
            [Section2ValidationErrors.provideFirstName]: findErrorWithKey(certificationStoreValidation.error?.details, 'firstName'),
            [Section2ValidationErrors.confirmESignAgreement]: findErrorWithKey(completeStoreValidation.error?.details, 'acceptedElectronicSignature')
        };
    }

    isRequestValid(): boolean {
        const isValidationResultValid = (result: Joi.ValidationResult): boolean => result.error === undefined;
        return (
            isValidationResultValid(this.validateCompleteStore(this.section2CompleteStore)) &&
            isValidationResultValid(this.validateCertificationStore(this.section2CertificationStore))
        );
    }

    async requestSection2SignAsync(): Promise<ESignResult> {
        try {
            if (!this.isRequestValid()) {
                return ESignResult.InvalidRequest;
            }

            const request = this.createRequest();

            const didSignSuccessfully = await this.remoteRepresentativeService.signI9Section2Async(request);
            return didSignSuccessfully ? ESignResult.Success : ESignResult.Failed;
        } catch (error) {
            // todo: report errors using a service or with log level, etc
            return ESignResult.Failed;
        }
    }
}



// WEBPACK FOOTER //
// ./src/services/onboarding-review/e-sign-request-service.ts